/**
 * Hub /residencias — PBSA España, ciudades indexables, prerender, sitemap y Firebase.
 */

const RESIDENCIAS_HUB_PATH = '/residencias';
const LEGACY_STUDENT_HUB_PATH = '/residencias-estudiantes';

/** Ciudades con página propia indexable (Fase 0) */
const RESIDENCIAS_INDEXED_CITY_SLUGS = [
  'madrid',
  'barcelona',
  'valencia',
  'malaga',
  'sevilla',
  'salamanca',
];

const RESIDENCIAS_INDEXED_CITY_SLUGS_SET = new Set(RESIDENCIAS_INDEXED_CITY_SLUGS);

/**
 * Operadores PBSA España (source en Firestore / scrapers).
 * Filtro de listados en /residencias vía providers= en searchProperties.
 */
const PBSA_ES_PROVIDER_IDS = [
  'resa',
  'nido',
  'xior',
  'livensa',
  'collegiate',
  'yugo',
  'urbancampus',
];

const RESIDENCIAS_PRERENDER_SOURCES = new Set([
  RESIDENCIAS_HUB_PATH,
  ...RESIDENCIAS_INDEXED_CITY_SLUGS.map((slug) => `${RESIDENCIAS_HUB_PATH}/${slug}`),
]);

function isResidenciasSearchPath(source) {
  if (!source || typeof source !== 'string') return false;
  if (source === RESIDENCIAS_HUB_PATH) return true;
  if (!source.startsWith(`${RESIDENCIAS_HUB_PATH}/`)) return false;
  return true;
}

function isLegacyStudentHubPath(source) {
  if (!source || typeof source !== 'string') return false;
  return source === LEGACY_STUDENT_HUB_PATH || source.startsWith(`${LEGACY_STUDENT_HUB_PATH}/`);
}

function isResidenciasOrLegacyStudentPath(source) {
  return isResidenciasSearchPath(source) || isLegacyStudentHubPath(source);
}

function getResidenciasPrerenderPaths() {
  return [RESIDENCIAS_HUB_PATH, ...RESIDENCIAS_INDEXED_CITY_SLUGS.map((s) => `${RESIDENCIAS_HUB_PATH}/${s}`)];
}

function isResidenciasCitySlugSeoIndexed(citySlug) {
  if (!citySlug) return true;
  return RESIDENCIAS_INDEXED_CITY_SLUGS_SET.has(String(citySlug).toLowerCase());
}

/** Redirects 301 — legacy, GSC y migración /residencias-estudiantes → /residencias */
function getResidenciasFirebaseRedirects() {
  const redirects = [];

  // Canónica nueva
  redirects.push({
    source: LEGACY_STUDENT_HUB_PATH,
    destination: RESIDENCIAS_HUB_PATH,
    type: 301,
  });
  redirects.push({
    source: `${LEGACY_STUDENT_HUB_PATH}/:city`,
    destination: `${RESIDENCIAS_HUB_PATH}/:city`,
    type: 301,
  });

  // Slugs legacy sin slash
  const legacyCityPairs = [
    ['residencias-estudiantes-madrid', 'madrid'],
    ['residencias-estudiantes-barcelona', 'barcelona'],
    ['residencia-estudiantes-madrid', 'madrid'],
    ['residencia-estudiantes-barcelona', 'barcelona'],
    ['residencias-universitarias-madrid', 'madrid'],
    ['residencias-universitarias-barcelona', 'barcelona'],
  ];
  for (const [source, city] of legacyCityPairs) {
    redirects.push({
      source: `/${source}`,
      destination: `${RESIDENCIAS_HUB_PATH}/${city}`,
      type: 301,
    });
  }

  // Habitaciones estudiantes → hub PBSA
  redirects.push({
    source: '/habitaciones-estudiantes',
    destination: RESIDENCIAS_HUB_PATH,
    type: 301,
  });
  redirects.push({
    source: '/habitaciones-estudiantes/:city',
    destination: `${RESIDENCIAS_HUB_PATH}/:city`,
    type: 301,
  });

  // Ciudades fuera del hub indexable → /residencias
  const nonIndexedSlugs = [
    'bilbao', 'granada', 'alicante', 'zaragoza', 'pamplona', 'tenerife', 'las-palmas',
    'san-sebastian', 'donostia', 'málaga', 'brooklyn', 'manhattan', 'new-york', 'jersey-city',
    'london', 'hammersmith', 'bloomsbury', 'walthamstow', 'mile-end', 'canada-water',
  ];
  for (const slug of nonIndexedSlugs) {
    redirects.push({
      source: `${RESIDENCIAS_HUB_PATH}/${slug}`,
      destination: RESIDENCIAS_HUB_PATH,
      type: 301,
    });
    redirects.push({
      source: `${LEGACY_STUDENT_HUB_PATH}/${slug}`,
      destination: RESIDENCIAS_HUB_PATH,
      type: 301,
    });
  }

  return redirects;
}

function isStaleResidenciasPrerenderRewrite(rewrite) {
  const dest = rewrite?.destination || '';
  if (!dest.startsWith('/prerendered/residencias')) return false;
  const source = rewrite?.source || '';
  if (RESIDENCIAS_PRERENDER_SOURCES.has(source)) return false;
  return isResidenciasSearchPath(source);
}

function isStaleLegacyStudentPrerenderRewrite(rewrite) {
  const dest = rewrite?.destination || '';
  if (!dest.startsWith('/prerendered/residencias-estudiantes')) return false;
  return true;
}

function isStaleResidenciasSpaFallback(rewrite) {
  const source = rewrite?.source || '';
  if (rewrite?.destination !== '/index.html') return false;
  if (RESIDENCIAS_PRERENDER_SOURCES.has(source)) return false;
  if (source === RESIDENCIAS_HUB_PATH) return false;
  if (source.startsWith(`${RESIDENCIAS_HUB_PATH}/`)) return true;
  if (isLegacyStudentHubPath(source)) return true;
  if (/^\/residencias-estudiantes-[a-z0-9-]+$/i.test(source)) return true;
  return false;
}

/** Query string de warmup/prerender para API search */
function getResidenciasSearchQuerySuffix() {
  return `&providers=${encodeURIComponent(PBSA_ES_PROVIDER_IDS.join(','))}`;
}

/** Landings SEO / GSC → canónica /residencias */
function getStudentSeoLandingRedirects() {
  const r = (source, destination) => ({ source, destination, type: 301 });
  const hub = RESIDENCIAS_HUB_PATH;
  const city = (slug) => `${hub}/${slug}`;

  return [
    r('/coliving-estudiantes', hub),
    r('/coliving-estudiantes/:city', `${hub}/:city`),
    r('/habitaciones-cerca-universidad', hub),
    r('/habitaciones-cerca-universidad/:city', `${hub}/:city`),
    r('/pisos-estudiantes-sin-fianza', hub),
    r('/habitaciones-baratas-estudiantes', hub),
    r('/habitaciones-baratas-estudiantes/:city', `${hub}/:city`),
    r('/habitaciones-estudiantes-universidad-complutense', city('madrid')),
    r('/habitaciones-estudiantes-uam', city('madrid')),
    r('/habitaciones-estudiantes-ucm', city('madrid')),
    r('/habitaciones-estudiantes-carlos-tercero', city('madrid')),
    r('/habitaciones-estudiantes-ub', city('barcelona')),
    r('/habitaciones-estudiantes-upc', city('barcelona')),
    r('/habitaciones-estudiantes-upf', city('barcelona')),
    r('/habitaciones-estudiantes-uv', city('valencia')),
    r('/habitaciones-estudiantes-us', city('sevilla')),
    r('/habitaciones-estudiantes-usal', city('salamanca')),
    r('/residencias-estudiantes/alcobendas', city('madrid')),
    r('/residencias-estudiantes/getafe', city('madrid')),
    r('/coliving-madrid-octubre-2026-estudiantes', city('madrid')),
  ];
}

function normalizeResidenciasRedirectDestination(destination) {
  if (!destination || typeof destination !== 'string') return destination;
  if (destination === LEGACY_STUDENT_HUB_PATH) return RESIDENCIAS_HUB_PATH;
  if (destination === `${LEGACY_STUDENT_HUB_PATH}/:city`) {
    return `${RESIDENCIAS_HUB_PATH}/:city`;
  }
  if (destination.startsWith(`${LEGACY_STUDENT_HUB_PATH}/`)) {
    return `${RESIDENCIAS_HUB_PATH}${destination.slice(LEGACY_STUDENT_HUB_PATH.length)}`;
  }
  return destination;
}

/** Reglas /residencias-estudiantes/{slug} → hub: obsoletas (wildcard :city o reglas canónicas). */
function isObsoleteLegacyPerCityHubRedirect(redirect) {
  const src = redirect?.source || '';
  const dest = redirect?.destination || '';
  if (!/^\/residencias-estudiantes\/[^:/]+$/.test(src)) return false;
  return dest === LEGACY_STUDENT_HUB_PATH || dest === RESIDENCIAS_HUB_PATH;
}

function redirectQualityScore(redirect) {
  const dest = redirect?.destination || '';
  if (dest.includes('residencias-estudiantes')) return 0;
  if (dest.startsWith(RESIDENCIAS_HUB_PATH)) return 2;
  return 1;
}

/**
 * Normaliza, deduplica y reordena redirects; elimina obsoletos residencias-estudiantes.
 * @returns {{ redirects: object[], stats: object }}
 */
function cleanupResidenciasFirebaseRedirects(existingRedirects) {
  const canonical = [
    ...getResidenciasFirebaseRedirects(),
    ...getStudentSeoLandingRedirects(),
  ];
  const canonicalBySource = new Map(canonical.map((item) => [item.source, item]));

  let normalized = 0;
  let dropped = 0;
  const merged = new Map();

  for (const raw of existingRedirects || []) {
    if (!raw?.source) continue;

    if (isObsoleteLegacyPerCityHubRedirect(raw)) {
      dropped += 1;
      continue;
    }

    const entry = {
      ...raw,
      destination: normalizeResidenciasRedirectDestination(raw.destination),
      type: raw.type || 301,
    };
    if (entry.destination !== raw.destination) normalized += 1;

    const prev = merged.get(entry.source);
    if (!prev || redirectQualityScore(entry) > redirectQualityScore(prev)) {
      merged.set(entry.source, entry);
    }
  }

  for (const [source, rule] of canonicalBySource) {
    merged.set(source, rule);
  }

  const redirects = [...merged.values()];
  const prioritySources = new Set(canonical.map((c) => c.source));
  redirects.sort((a, b) => {
    const aPri = prioritySources.has(a.source) ? 0 : 1;
    const bPri = prioritySources.has(b.source) ? 0 : 1;
    if (aPri !== bPri) return aPri - bPri;
    return a.source.localeCompare(b.source);
  });

  return {
    redirects,
    stats: { normalized, dropped, canonical: canonical.length, total: redirects.length },
  };
}

/**
 * Quita headers noindex en rutas legacy; aplica reglas /residencias/{ciudad} no indexables.
 */
function cleanupResidenciasFirebaseHeaders(existingHeaders, getStudentNonIndexedHeaderRules) {
  const studentRules = getStudentNonIndexedHeaderRules ? getStudentNonIndexedHeaderRules() : [];
  const studentSources = new Set(studentRules.map((r) => r.source));

  let legacyNoindexRemoved = 0;
  const kept = [];

  for (const rule of existingHeaders || []) {
    const src = rule?.source || '';
    if (src === LEGACY_STUDENT_HUB_PATH || src.startsWith(`${LEGACY_STUDENT_HUB_PATH}/`)) {
      legacyNoindexRemoved += 1;
      continue;
    }

    if (studentSources.has(src)) {
      const otherHeaders = (rule.headers || []).filter((h) => h.key !== 'X-Robots-Tag');
      if (otherHeaders.length > 0) {
        kept.push({ ...rule, headers: otherHeaders });
      }
      continue;
    }

    kept.push(rule);
  }

  return {
    headers: [...kept, ...studentRules],
    stats: { legacyNoindexRemoved, noindexResidencias: studentRules.length },
  };
}

module.exports = {
  RESIDENCIAS_HUB_PATH,
  LEGACY_STUDENT_HUB_PATH,
  RESIDENCIAS_INDEXED_CITY_SLUGS,
  RESIDENCIAS_INDEXED_CITY_SLUGS_SET,
  PBSA_ES_PROVIDER_IDS,
  RESIDENCIAS_PRERENDER_SOURCES,
  isResidenciasSearchPath,
  isLegacyStudentHubPath,
  isResidenciasOrLegacyStudentPath,
  getResidenciasPrerenderPaths,
  isResidenciasCitySlugSeoIndexed,
  getResidenciasFirebaseRedirects,
  isStaleResidenciasPrerenderRewrite,
  isStaleLegacyStudentPrerenderRewrite,
  isStaleResidenciasSpaFallback,
  getResidenciasSearchQuerySuffix,
  getStudentSeoLandingRedirects,
  normalizeResidenciasRedirectDestination,
  cleanupResidenciasFirebaseRedirects,
  cleanupResidenciasFirebaseHeaders,
};
